import * as React from 'react';
import { IAMessageVideo } from '../video/MessageVideo';
import { IAButton, IAIcon, IALikes, IMessage } from '../../..';
import { makeStyles } from 'office-ui-fabric-react';
import { ILike } from '../../../interfaces/ILike';
import { IUser } from '../../../interfaces/IUser';
import { IAUniqueViews } from '../../uniqueViews/UniqueViews';
import { IAMessageCategories } from '../../viewer/categories/MessageCategories';
import { IFontSlots } from '../../../interfaces/IFontSlots';
import { LocalizationService } from '../../../services/LocalizationService';
import { Helper } from '../../../Helper';
import { LoggingService } from '../../../services/LoggingService';
import { ESC_KEY } from '../../../global/Constants';

export interface IReelsPopupProps {
  message: IMessage;
  environment: "Production" | "Development";
  tenant: string;
  token: string;
  highlightColor: string;
  rollupHighlightColor: string;
  user: IUser;
  type: string;
  instanceName?: string;
  source?: "Home" | "Mobile" | "SharePoint";
  showEmojiPickerExpandButton?: boolean;
  likesUpdated: (likes: ILike[]) => void;
  isMobile?: boolean;
  reactionOptions?: any[];
  fontSlots?: IFontSlots;
  likesEnabled?: boolean;
  showUniqueViews?: boolean;
  showCategories?: boolean;
  onClose: () => void;
  onShowNext?: () => void;
  onEdit?: () => void;
}

const useClasses = makeStyles({
  popupBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 900003,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
  },
  popupContent: {
    position: 'relative',
    width: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#000000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // flex-start, not center: with overflow:auto, justify-content:center clips/hides content on both ends instead of letting it scroll into view
    justifyContent: 'flex-start',
  },
  popupWrapper: {
    position: 'relative',
  },
  metadata: {
    width: '100%',
    backgroundColor: '#ffffff',
    color: '#000000',
    padding: '20px',
    boxSizing: 'border-box',
    bottom: 0,
  },
  metadataTitle: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '7px',
  },
  metadataTeaser: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  socialFunctions: {
    marginTop: '15px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '15px',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '5px',
  },
  copyAction: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  copyActionText: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
    marginTop: '3px',
  },
  actionButtons: {
    marginLeft: 'auto',
    marginTop: '5px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  showNextButton: {
    marginLeft: 'auto',
    border: '1px solid',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    lineHeight: 1.2,
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(50%, -50%)',
    background: 'rgba(255,255,255,0.9)',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
  },
});

export function ReelsPopup(props: IReelsPopupProps): JSX.Element {
  const styles = useClasses();
  const localizationService = React.useMemo(() => new LocalizationService(), []);
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const [linkCopied, setLinkCopied] = React.useState(false);
  const [copyHovered, setCopyHovered] = React.useState(false);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const metadataRef = React.useRef<HTMLDivElement>(null);
  const [videoSize, setVideoSize] = React.useState<{ width: number; height: number } | null>(null);
  React.useEffect(() => {
    localizationService.checkLocalizedStrings().then(() => forceUpdate());
  }, []);

  React.useEffect(() => {
    popupRef.current?.focus();
  }, []);

  // Shrinks the 9:16 video (keeping its width in sync with the metadata panel) just enough
  // so the metadata is never clipped, instead of giving the video a fixed max-height.
  React.useLayoutEffect(() => {
    const recalc = () => {
      const maxWidth = props.isMobile ? window.innerWidth - 80 : 500;
      const maxHeight = props.isMobile ? window.innerHeight - 220 : window.innerHeight - 80;
      const metadataHeight = metadataRef.current?.offsetHeight ?? 0;
      const availableVideoHeight = Math.max(120, maxHeight - metadataHeight);
      const width = Math.min(maxWidth, availableVideoHeight * 9 / 16);
      setVideoSize({ width, height: width * 16 / 9 });
    };
    recalc();
    window.addEventListener('resize', recalc);
    // Re-measure whenever the metadata panel's actual rendered height changes (e.g. web font
    // finishing loading after the first measurement, categories wrapping, teaser text updates)
    const node = metadataRef.current;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recalc) : undefined;
    if (node && observer) {
      observer.observe(node);
    }
    return () => {
      window.removeEventListener('resize', recalc);
      observer?.disconnect();
    };
  }, [props.isMobile, props.message?.id, props.message?.title, props.message?.teaser, props.message?.content]);

  // Track views
  React.useEffect(() => {
    if (LoggingService !== undefined) {
      LoggingService.trackEvent("OpenMessage", {
        ActionType: "OpenMessage",
        Component: "Messages",
        Id: props.message?.id,
        Title: props.message?.title,
        Categories: props.message?.categories,
        StartDate: props.message?.startDate,
        Created: props.message?.created,
        Author: props.message?.author,
        Tenant: props.tenant,
        Instance: props.instanceName,
        UserId: props.token,
        Environment: props.environment
      });
    }
  }, []);

  const copyLink = () => {
    const nav: any = window.navigator;
    const url = window.location.href.split('?')[0];
    const link = `${url}?instance=${props.instanceName}&messageId=${props.message?.id}`;
    nav.clipboard?.writeText(link)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err: any) => console.error('Could not copy link:', err));
  };

  const getTeaserText = (): string => {
    let content = props.message?.content;
    if (!content) {
      content = '';
    }
    const div = document.createElement('div');
    div.innerHTML = content
      .replace(/<\/p>/g, '</p> ')
      .replace(/<\/h1>/g, '</h1> ')
      .replace(/<\/h2>/g, '</h2> ')
      .replace(/<\/li>/g, '</li> ');
    div.innerHTML = div.innerHTML.replace(/\>/g, '"/> ');
    div.innerHTML = div.innerHTML.replace(/"  "/g, ' ');
    return props.message?.teaser ? props.message?.teaser : (div.textContent || div.innerText || '');
  };
  let videoUrl = props.message?.videoUrl;
  if ((location.href.includes("sharepoint.com") && props.message?.videoEmbedCode) || (videoUrl == undefined && props.message?.videoEmbedCode)) {
    // Unlike the reels grid/card previews, the popup should autoplay its video.
    videoUrl = Helper.getVideoSrcFromEmbedCode(props.message.videoEmbedCode);
  }
  return (
    <div
      ref={popupRef}
      className={styles.popupBackdrop}
      tabIndex={-1}
      onClick={props.onClose}
      onKeyDown={(event) => {
        if (event.key === ESC_KEY) {
          event.preventDefault();
          event.stopPropagation();
          props.onClose();
        }
      }}
    >
      <div
        className={styles.popupWrapper}
        style={{
          width: videoSize ? `${videoSize.width}px` : (props.isMobile ? 'calc(100% - 80px)' : '500px'),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Safety net only: the video is pre-shrunk to fit alongside the metadata, so this shouldn't normally need to clip/scroll */}
        <div
          className={styles.popupContent}
          style={{
            maxHeight: props.isMobile ? 'calc(100vh - 220px)' : 'calc(100vh - 80px)',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
        {(props.message?.videoUrl || props.message?.videoEmbedCode) &&
          <div style={{ width: videoSize ? `${videoSize.width}px` : '100%', height: videoSize ? `${videoSize.height}px` : undefined, aspectRatio: videoSize ? undefined : '9 / 16', flexShrink: 0, position: 'relative' }}>
            <IAMessageVideo
              videoUrl={videoUrl}
              title={props.message?.title}
              width={'100%'}
              height={'100%'}
              environment={props.environment}
              tenant={props.tenant}
              token={props.token}
              isPlaying={true}
            />
          </div>
        }
        <div className={styles.metadata} ref={metadataRef}>
          {props.showCategories && props.message?.categories && props.message?.categories.length > 0 &&
            <IAMessageCategories
              categories={props.message?.categories}
              showOverflowCounter
              disablePopup={props.isMobile}
              fontSlots={props.fontSlots}
              textColorWhenTransparentBackground={'#000000'}
            />
          }
          <h2 className={styles.metadataTitle} style={{ fontFamily: props.fontSlots?.heading?.fontFamily }}>{props.message?.title}</h2>
          <p className={styles.metadataTeaser} style={{ fontFamily: props.fontSlots?.body?.fontFamily }}>{getTeaserText()}</p>
          <div className={styles.socialFunctions}>
            {props.likesEnabled && (
              <IALikes
                highlightColor={props.rollupHighlightColor}
                color={props.rollupHighlightColor}
                itemId={props.message?.id ? `${props.message?.id}` : ''}
                user={props.user}
                tenant={props.tenant}
                component={props.type}
                instance={props.instanceName ?? ''}
                token={props.token}
                environment={props.environment}
                source={props.source ?? ''}
                likes={props.message?.likes}
                showEmojiPickerExpandButton={props.showEmojiPickerExpandButton ?? true}
                reactionOptions={props.reactionOptions ?? []}
                likesUpdated={(likes: ILike[]) => props.likesUpdated(likes)}
                displayVertical={false}
                isMobile={props.isMobile}
                style={{ marginTop: '5px' }}
              />
            )}
            {props.showUniqueViews && (
              <IAUniqueViews
                color={props.rollupHighlightColor}
                itemId={props.message?.id ? `${props.message?.id}` : ''}
                user={props.user}
                tenant={props.tenant}
                component={props.type === 'Messages' ? 'Message' : 'Event'}
                instance={props.instanceName ?? ''}
                token={props.token}
                environment={props.environment}
                source={props.source ?? ''}
                uniqueViews={props.message?.uniqueViews}
                displayVertical={false}
                style={{ marginTop: '10px' }}
              />
            )}
            <div
              className={styles.copyAction}
              onMouseEnter={() => setCopyHovered(true)}
              onMouseLeave={() => setCopyHovered(false)}
            >
              <IAIcon
                url={linkCopied
                  ? 'https://intraactivestorage.blob.core.windows.net/cdn/icons/Checkmark/SVG/ic_fluent_checkmark_24_regular.svg'
                  : 'https://intraactivestorage.blob.core.windows.net/cdn/icons/Copy/SVG/ic_fluent_copy_24_regular.svg'
                }
                size={18}
                color={props.rollupHighlightColor}
                style={{
                  width: 17,
                  float: 'left',
                  marginTop: '10px',
                }}
                onClick={copyLink}
              />
              {(linkCopied || copyHovered) && (
                <span className={styles.copyActionText} style={{ color: props.rollupHighlightColor }}>
                  {linkCopied
                    ? (localizationService.strings?.Panel_NavigatorBar_LinkCopied ?? 'Link copied')
                    : (localizationService.strings?.Panel_NavigatorBar_CopyLink ?? 'Copy Link')}
                </span>
              )}
            </div>
            {(props.onEdit !== undefined || props.onShowNext !== undefined) && (
              <div className={styles.actionButtons}>
                {props.onEdit !== undefined && (
                  <IAButton
                    label={(localizationService.strings as any)?.Messages_Edit ?? 'Edit'}
                    onClick={props.onEdit}
                    buttonColor="transparent"
                    borderColor={props.rollupHighlightColor}
                    textColor={props.rollupHighlightColor}
                    style={{
                      borderRadius: '4px',
                      padding: '6px 12px',
                      height: 'auto',
                      minHeight: 0,
                      lineHeight: 1.2,
                    }}
                  />
                )}
                {props.onShowNext !== undefined && (
                  <IAButton
                    label={(localizationService.strings as any)?.Messages_ShowNext ?? 'Show next'}
                    onClick={props.onShowNext}
                    buttonColor="transparent"
                    borderColor={props.rollupHighlightColor}
                    textColor={props.rollupHighlightColor}
                    style={{
                      borderRadius: '4px',
                      padding: '6px 12px',
                      height: 'auto',
                      minHeight: 0,
                      lineHeight: 1.2,
                    }}
                  />
                )}
              </div>
            )}

          </div>
        </div>
      </div>
      <button onClick={props.onClose} className={styles.closeButton}>
        <IAIcon
          url="https://intraactivestorage.blob.core.windows.net/cdn/icons/Dismiss/SVG/ic_fluent_dismiss_48_regular.svg"
          color={props.highlightColor}
          size={18}
          style={{ marginTop: '3px' }}
        />
      </button>
      </div>
    </div>
  );
}
